import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { MachineBaseDto, MachineCard } from 'src/app/types/machine';
import { WINDOW } from '../window/window.service';
import { ClientService } from '../client/client.service';
import { StorageService } from '../storage/storage.service';

@Injectable({ providedIn: 'root' })
export class TrackingService {

  constructor(
    @Inject(WINDOW) private window: Window,
    @Inject(PLATFORM_ID) private platformId: Object,
    private clientService: ClientService,
    private Storage: StorageService,
  ) {
    (this.window as any).dataLayer = (this.window as any).dataLayer || [];

    this.gtag('consent', 'default', {
      ad_storage: 'denied',
      analytics_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });

    const consent = this.Storage.get('cookie_consent');
    if (consent) {
      this.updateConsent(consent as any);
    }
  }

  private gtag(
    command: 'consent',
    action: 'default' | 'update',
    params: {
      ad_storage?: 'granted' | 'denied';
      analytics_storage?: 'granted' | 'denied';
      ad_user_data?: 'granted' | 'denied';
      ad_personalization?: 'granted' | 'denied';
    }
  ) {
    (this.window as any).dataLayer.push([command, action, params]);
  }



  public updateConsent(consent: 'accepted' | 'rejected') {
    this.Storage.set('cookie_consent', consent);
    const grant = consent === 'accepted' ? 'granted' : 'denied';
    this.gtag('consent', 'update', {
      ad_storage: grant,
      analytics_storage: grant,
      ad_user_data: grant,
      ad_personalization: grant
    });
  }

  generateId(length: number = 15): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let id = '';
    for (let i = 0; i < length; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  }

  private push(event: object): void {
    if (!isPlatformBrowser(this.platformId)) return;
    (this.window as any).dataLayer = (this.window as any).dataLayer || [];
    (this.window as any).dataLayer.push(event);
  }

  pageView(path: string): void {
    this.push({
      event: 'page_view',
      page_path: path,
    });
  }

  newsletter(email: string): void {
    if (!this.clientService.client?.analytics?.newsletterSubscription) { return; }
    this.push({
      event: 'subscribe',
      customer: { email }
    });
  }

  contactForm(machine: MachineBaseDto, email: string): void {
    if (!this.clientService.client?.analytics?.contactFormSubmission) { return; }
    const { ecommerce } = this.getEcommerceParams(machine);
    this.push({
      event: 'generate_lead',
      content_name: 'product',
      customer: { email },
      ecommerce,
      event_id: this.generateId(), 
    });
  }

  viewItem(machine: MachineBaseDto): void {
    if (!this.clientService.client?.analytics?.viewItem) { return; }
    const { ecommerce } = this.getEcommerceParams(machine);
    this.push({
      event: 'view_item',
      content_name: 'product',
      ecommerce,
      event_id: this.generateId(),
    });
  }

  private getEcommerceParams(machine: MachineBaseDto) {
    const params = this.transformMachineCards(machine.cards);
    const price = this.getMachinePrice(machine);
    return {
      ecommerce: {
        currency:price.currency,
        value: price.value,
        items: [
          {
            item_id: machine.machinericId,
            item_name: machine.type,
            item_brand: params['BRAND']?.parameterValue || '',
            item_category: machine.subcategory,
            item_model: params['MODEL']?.parameterValue || '',
            item_year_of_manufacture: params['YEAR_OF_MANUFACTURE']?.parameterValue || '',
            item_status: machine.status?.status,
            engine_type: params['ENGINE_TYPE']?.parameterValue || '',
            location_id: `${machine.location?.city}, ${machine.location?.countryCode}`,
            price: price.value,
            quantity: 1,
            item_vin: params['VIN_CODE']?.parameterValue || '',
          }
        ]
      }
    }
  }

  private getMachinePrice(machine: MachineBaseDto) {
    const priceObj = machine.prices.filter(i => !i.isStrikethroughPrice)[0];
    const value = priceObj?.priceExcludingVat || undefined;
    const currency = priceObj?.currency || undefined;
    return {
      value,
      currency,
    }
  }

  private transformMachineCards(cards: MachineCard[]) {
    const out: any = {};
    for (const card of cards) {
      for (const subgroup of card.subgroups) {
        for (const parameter of subgroup.parameters) {
          out[parameter.parameterKey] = {
            ...parameter,
          };
        }
      }
    }
    return out;
  }
}
